
import React, { useEffect, useRef, useState } from "react";

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
export const VARIANTS = {
    fadeUp:    { from: 'translateY(32px)',  to: 'translateY(0)' },
    fadeDown:  { from: 'translateY(-24px)', to: 'translateY(0)' },
    fadeLeft:  { from: 'translateX(-32px)', to: 'translateX(0)' },
    fadeRight: { from: 'translateX(32px)',  to: 'translateX(0)' },
    zoomIn:    { from: 'scale(0.92)',       to: 'scale(1)'      },
    fadeOnly:  { from: 'scale(1)',          to: 'scale(1)'      },
};

export const BIOMEDICAL_WASTE_COLORS = {
    Red: '#ef4444', Blue: '#3b82f6', Yellow: '#facc15',
    Black: '#000000', White: '#cbe5e9ff', Cytotoxic: '#8F00FF',
};

export const DEFAULT_COLORS = [
    '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
    '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#14b8a6',
];

// ─── SHARED AXIS STYLES ───────────────────────────────────────────────────────
export const axisLabelStyle = {
    fontSize: '11px', fontWeight: 500,
    colors: '#64748b', fontFamily: "'DM Sans', system-ui, sans-serif",
};
export const monoLabelStyle = {
    fontSize: '10.5px', fontWeight: 500,
    colors: '#94a3b8', fontFamily: "'DM Mono', monospace",
};

// ─────────────────────────────────────────────────────────────────────────────
//  ANIMATION WRAPPER
// ─────────────────────────────────────────────────────────────────────────────
export const ChartAnimationWrapper = ({
    children, variant = 'fadeUp', delay = 0, duration = 600, threshold = 0.12,
}) => {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        if (typeof IntersectionObserver === 'undefined') { setVisible(true); return; }
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
            { threshold },
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [threshold]);

    const { from, to } = VARIANTS[variant] || VARIANTS.fadeUp;
    return (
        <div ref={ref} style={{
            opacity:    visible ? 1 : 0,
            transform:  visible ? to : from,
            transition: visible
                ? `opacity ${duration}ms cubic-bezier(0.22,1,0.36,1) ${delay}ms, transform ${duration}ms cubic-bezier(0.22,1,0.36,1) ${delay}ms`
                : 'none',
            willChange: 'opacity, transform',
        }}>
            {children}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
//  NUMBER FORMATTERS
// ─────────────────────────────────────────────────────────────────────────────
export const formatNumber = (val) => {
    if (val === null || val === undefined || val === '' || isNaN(val)) return 0;
    if (typeof val === 'number') return Number(val.toFixed(2));
    const parsed = parseFloat(val);
    return isNaN(parsed) ? 0 : Number(parsed.toFixed(2));
};

export const formatNumberWithSuffix = (val) => {
    if (val === null || val === undefined) return val;
    if (typeof val !== 'number') return val;
    if (isNaN(val)) return '—';
    const isFloat = (v) => !Number.isInteger(v);
    if (Math.abs(val) >= 1_000) {
        const kVal = val / 1_000;
        return isFloat(kVal) ? `${parseFloat(kVal.toFixed(2))}K` : `${kVal}K`;
    }
    return isFloat(val) ? `${parseFloat(val.toFixed(2))}` : `${val}`;
};

export const formatYAxisSmart = (val) => {
    if (val === null || val === undefined || isNaN(val)) return '';
    const n = typeof val === 'number' ? val : parseFloat(val);
    return isNaN(n) ? '' : formatNumberWithSuffix(n);
};

export const formatDataArray = (arr) => {
    if (!Array.isArray(arr)) return arr;
    return arr.map(val => (typeof val === 'number' ? formatNumber(val) : val));
};

export const formatSeries = (series) => {
    if (!Array.isArray(series)) return series;
    return series.map(s => ({
        ...s,
        data: Array.isArray(s.data) ? formatDataArray(s.data) : s.data,
    }));
};

// ─────────────────────────────────────────────────────────────────────────────
//  SERIES / UNIT HELPERS
// ─────────────────────────────────────────────────────────────────────────────
export const getSeriesColors = (series, colorMap, defaultColors = []) => {
    if (!series) return [];
    return series.map((s, i) =>
        colorMap?.[s.name] ? colorMap[s.name] : (s.color || defaultColors[i % defaultColors.length])
    );
};

export const shouldBeStacked = (series, colorMap) => {
    if (!series || series.length <= 1) return false;
    if (!colorMap || Object.keys(colorMap).length === 0) return false;
    return series.length === Object.keys(colorMap).length;
};

export const getSingleUnit = (unitsMap) => {
    if (!unitsMap) return null;
    const units = [...new Set(Object.values(unitsMap).filter(Boolean))];
    return units.length === 1 ? units[0] : null;
};

export const sanitiseSeriesName = (name) => {
    if (!name) return name;
    const cleaned = name.toString().replace(/^series[-_\s]?\d+$/i, '').trim();
    return cleaned || name;
};

// ─────────────────────────────────────────────────────────────────────────────
//  detectUnitField — PRIORITY-BASED: kpi > sub_category > category
// ─────────────────────────────────────────────────────────────────────────────
const UNIT_FIELD_PRIORITY = ['kpi', 'sub_category', 'category'];

export const detectUnitField = (rows, xAxisField, stackByField) => {
    if (!rows || rows.length === 0) return { unitField: null, unitsMap: {} };

    const candidates    = [xAxisField, stackByField].filter(Boolean);
    const fieldUnitSets = {};

    for (const field of candidates) {
        fieldUnitSets[field] = {};
        for (const row of rows) {
            const val  = String(row[field] ?? '');
            const unit = row.unit || '';
            if (!fieldUnitSets[field][val]) fieldUnitSets[field][val] = new Set();
            if (unit) fieldUnitSets[field][val].add(unit);
        }
    }

    const buildUnitsMap = (field) => {
        const map = {};
        for (const [val, unitSet] of Object.entries(fieldUnitSets[field] || {})) {
            const arr = [...unitSet];
            if (arr.length) map[val] = arr[0];
        }
        return map;
    };

    const fieldHasUnits = (field) =>
        Object.values(fieldUnitSets[field] || {}).some(s => s.size > 0);

    for (const priorityName of UNIT_FIELD_PRIORITY) {
        const matched = candidates.find(f => f === priorityName);
        if (matched && fieldHasUnits(matched)) {
            return { unitField: matched, unitsMap: buildUnitsMap(matched) };
        }
    }

    const score = (field) => {
        const sizes = Object.values(fieldUnitSets[field] || {}).map(s => s.size);
        return sizes.length ? Math.max(...sizes) : Infinity;
    };
    let unitField = xAxisField, best = Infinity;
    for (const field of candidates) {
        const s = score(field);
        if (s < best) { best = s; unitField = field; }
    }
    return { unitField, unitsMap: buildUnitsMap(unitField) };
};

// ─────────────────────────────────────────────────────────────────────────────
//  BASE CHART CONFIG FACTORY
// ─────────────────────────────────────────────────────────────────────────────
export const getBaseChartConfig = (theme = 'light', customColors = null, data = {}, toolbarVisible = false) => {
    const sharedUnit    = getSingleUnit(data.unitsMap);
    const hasMixedUnits = !sharedUnit && data.unitsMap && Object.keys(data.unitsMap).length > 0;

    return {
        chart: {
            fontFamily: "'DM Sans', system-ui, sans-serif",
            parentHeightOffset: 25, offsetY: 20,
            zoom: { enabled: !!toolbarVisible },
            toolbar: {
                offsetY: 5, show: toolbarVisible,
                tools: {
                    download: !!toolbarVisible, selection: !!toolbarVisible,
                    zoom: !!toolbarVisible, zoomin: !!toolbarVisible,
                    zoomout: !!toolbarVisible, pan: !!toolbarVisible, reset: !!toolbarVisible,
                },
            },
            animations: {
                enabled: true, easing: 'easeinout', speed: 900,
                animateGradually: { enabled: true, delay: 100 },
                dynamicAnimation: { enabled: true, speed: 450 },
            },
            background: 'transparent', dropShadow: { enabled: false },
        },
        colors: customColors || [
            '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
            '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#14b8a6', '#8b5a87', '#059669',
        ],
        stroke:     { show: true, width: 3, colors: ['#ffffff'] },
        dataLabels: {
            enabled: true,
            style:   { fontSize: '11px', fontWeight: 700, fontFamily: "'DM Mono', monospace", colors: ['#374151'] },
            dropShadow: { enabled: false },
            formatter: (val) => formatNumberWithSuffix(val),
        },
        tooltip: {
            theme, style: { fontSize: '13px', fontFamily: "'DM Sans', system-ui, sans-serif" },
            fillSeriesColor: false, marker: { show: true }, shared: true, intersect: false,
            y: {
                formatter: (val) => {
                    const f = formatNumberWithSuffix(val);
                    return sharedUnit ? `${f} ${sharedUnit}` : f;
                },
                title: {
                    formatter: (seriesName) => {
                        const clean = sanitiseSeriesName(seriesName);
                        if (!clean) return '';
                        if (hasMixedUnits && data.unitsMap) {
                            const unit = data.unitsMap[seriesName] ?? data.unitsMap[clean];
                            if (unit) return `${clean} (${unit}):`;
                        }
                        return `${clean}:`;
                    },
                },
            },
        },
        grid: {
            borderColor: '#f1f5f9', strokeDashArray: 4,
            xaxis: { lines: { show: true } }, yaxis: { lines: { show: true } },
            padding: { top: -10, right: 20, bottom: 8, left: 20 },
        },
        legend: {
            show: true, showForSingleSeries: true, position: 'bottom',
            fontSize: '12px', fontFamily: "'DM Sans', system-ui, sans-serif", fontWeight: 500,
            labels: { colors: '#475569' },
            markers: { width: 10, height: 10, radius: 3 },
            itemMargin: { horizontal: 14, vertical: 6 },
            onItemClick:  { toggleDataSeries: true },
            onItemHover:  { highlightDataSeries: true },
            formatter: (seriesName) => {
                const clean = sanitiseSeriesName(seriesName);
                if (!data.unitsMap) return clean;
                if (sharedUnit) return clean;
                const unit = data.unitsMap[seriesName];
                return unit ? `${clean} (${unit})` : clean;
            },
        },
    };
};

// ─────────────────────────────────────────────────────────────────────────────
//  ERROR MESSAGE
// ─────────────────────────────────────────────────────────────────────────────
export const ErrorMessage = ({ message = "No data available to display" }) => (
    <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        minHeight: '320px', padding: '48px 40px',
        background: 'linear-gradient(145deg, #f8fafc 0%, #f1f5f9 100%)',
        borderRadius: '14px', border: '1px solid #e2e8f0',
        fontFamily: "'DM Sans', system-ui, sans-serif", position: 'relative', overflow: 'hidden',
    }}>
        <div style={{ position: 'absolute', inset: 0, opacity: 0.35, backgroundImage: 'radial-gradient(circle, #cbd5e1 1px, transparent 1px)', backgroundSize: '24px 24px', pointerEvents: 'none' }} />
        <div style={{ width: 68, height: 68, borderRadius: 18, background: '#fff', border: '1.5px solid #e2e8f0', boxShadow: '0 4px 16px rgba(15,23,42,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18, position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 28 }}>
                {[60, 100, 40, 80].map((h, i) => (
                    <div key={i} style={{ width: 8, height: `${h}%`, borderRadius: 3, background: i === 1 ? '#94a3b8' : '#cbd5e1' }} />
                ))}
            </div>
        </div>
        <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#334155', margin: '0 0 8px', textAlign: 'center', position: 'relative', zIndex: 1 }}>{message}</h3>
        <p  style={{ fontSize: '12.5px', color: '#94a3b8', margin: '0 0 20px', textAlign: 'center', maxWidth: '300px', lineHeight: 1.6, position: 'relative', zIndex: 1 }}>Try adjusting your filters.</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '10px', color: '#d1d5db', letterSpacing: '0.08em', textTransform: 'uppercase', fontFamily: "'DM Mono', monospace", position: 'relative', zIndex: 1 }}>
            <div style={{ width: 24, height: 1, background: '#e2e8f0' }} />No data<div style={{ width: 24, height: 1, background: '#e2e8f0' }} />
        </div>
    </div>
);
